import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { RefreshCw, Building2, Activity, AlertTriangle, MinusCircle } from "lucide-react";

const STATUS_META = {
  actif:    { label: "Actif",    Icon: Activity,      dot: "bg-[#10D183]", text: "text-[#065F46]", chip: "bg-[#D1FAE5] border-[#A7F3D0]" },
  faible:   { label: "Faible",   Icon: AlertTriangle, dot: "bg-[#F9C80E]", text: "text-[#92400E]", chip: "bg-[#FEF3C7] border-[#FDE68A]" },
  inactif:  { label: "Inactif",  Icon: MinusCircle,   dot: "bg-[#EF4444]", text: "text-[#991B1B]", chip: "bg-[#FEE2E2] border-[#FECACA]" },
};

export function AgenciesStatsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stats/agencies");
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const counts = data?.counts || { actif: 0, faible: 0, inactif: 0 };
  const agencies = data?.agencies || [];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_8px_24px_rgba(57,125,255,0.06)] p-6 sm:p-7" data-testid="agencies-stats">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#397DFF]" />
            <h2 className="font-display text-2xl font-bold text-[#191D1E]">Performance agences</h2>
          </div>
          <Button variant="outline" onClick={reload} data-testid="agencies-refresh" className="rounded-xl border-[#E5E7EB] hover:bg-[#F5F6F9]">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </Button>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          {["actif", "faible", "inactif"].map((k) => {
            const m = STATUS_META[k];
            return (
              <div key={k} className={`rounded-xl border ${m.chip} p-4 flex items-center gap-3`} data-testid={`agency-kpi-${k}`}>
                <m.Icon className={`w-6 h-6 ${m.text}`} />
                <div>
                  <div className={`text-2xl font-bold font-display ${m.text} leading-none`}>{counts[k]}</div>
                  <div className={`text-xs ${m.text} mt-1 font-semibold uppercase tracking-wide`}>{m.label.toLowerCase()}{counts[k] !== 1 ? "s" : ""}</div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm" data-testid="agencies-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#939BA0] border-b border-[#E5E7EB]">
                <th className="px-2 py-2">Agence (zone)</th>
                <th className="px-2 py-2">Licencié</th>
                <th className="px-2 py-2 text-right">Commerçants</th>
                <th className="px-2 py-2 text-right">Leads</th>
                <th className="px-2 py-2 text-right">Convertis</th>
                <th className="px-2 py-2 text-right">Taux conv.</th>
                <th className="px-2 py-2 text-right">Cashback engagé</th>
                <th className="px-2 py-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {agencies.map((a) => {
                const m = STATUS_META[a.status] || STATUS_META.inactif;
                return (
                  <tr key={`${a.scope}-${a.zone}`} className="border-b border-[#EEF0F4] last:border-0" data-testid={`agency-row-${a.zone}`}>
                    <td className="px-2 py-3">
                      <div className="font-semibold text-[#191D1E]">{a.zone}</div>
                      {a.parrainage_enabled && (
                        <div className="text-[10px] uppercase tracking-wider text-[#9D174D] mt-0.5 font-semibold">Parrainage activé</div>
                      )}
                    </td>
                    <td className="px-2 py-3 text-[#687175]">{a.licencie_name || <span className="text-[#BFC6CC]">—</span>}</td>
                    <td className="px-2 py-3 text-right text-[#191D1E] tabular-nums">{a.merchants_count}</td>
                    <td className="px-2 py-3 text-right text-[#191D1E] tabular-nums">{a.leads_total}</td>
                    <td className="px-2 py-3 text-right text-[#191D1E] tabular-nums">{a.leads_converti}</td>
                    <td className="px-2 py-3 text-right text-[#191D1E] tabular-nums">{a.leads_total > 0 ? `${a.taux_conversion}%` : "—"}</td>
                    <td className="px-2 py-3 text-right text-[#191D1E] tabular-nums">{a.cashback_engage} €</td>
                    <td className="px-2 py-3">
                      <Badge className={`${m.chip} ${m.text} border gap-1.5`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
                        {m.label}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!loading && agencies.length === 0 && (
          <div className="text-center text-sm text-[#939BA0] py-10" data-testid="agencies-empty">Aucune agence pour l'instant.</div>
        )}

        <p className="text-xs text-[#939BA0] mt-5 italic">
          <strong>Statut :</strong> Actif = ≥ 10 leads sur 30 j ou ≥ 1 conversion ce mois · Faible = 1-9 leads sur 30 j · Inactif = 0 lead sur 30 j.
        </p>
      </div>
    </div>
  );
}

export default AgenciesStatsPanel;
